import Vue, { CreateElement } from 'vue'
import UserClubSummary from '@src/data/user-club-summary'
import Leaderboard from '@src/data/leader-board'
import { UserClubSummaryService } from '@src/service/user-club-summary'
import { Component } from 'vue-property-decorator'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import camelcaseKeys from 'camelcase-keys'
import { MemberProfile } from '@src/data/member-profile'
import Club from '@src/data/club'

// Define the response type
type MemberResponse = { profile: any }

import './index.sass'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Icon } from '@src/elements/icon/icon'
import { Divider } from '@src/elements/divider/divider'
import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import { OptionGroup } from '@src/elements/select/option-group'
import { Loader } from '@src/components/loading-screen/loader/loader'

@Component({
  computed: {
    stations () {
      return this.$store.state.stations
    },

    userClubSummary () {
      return this.$store.state.userClubSummary
    },

    leaderBoard () {
      return this.$store.state.leaderBoard
    },

    member () {
      return this.$store.state.memberProfile
    }
  }
}
)
export class MyClub extends Vue {
  leaderBoard: Leaderboard
  userClubSummary: UserClubSummary
  userClubService: UserClubSummaryService
  userClubSummaryLoading: boolean = false
  leaderBoardLoading: boolean = false
  member: any
  memberProfileLoading: boolean = false
  apiClient: ApiClient = new ApiClient()

  myClub: boolean = true
  allClubs: boolean = false
  dateFilter: string = 'today'

  async mounted () {
    this.userClubService = new UserClubSummaryService()
    await this.loadMemberProfile()
    await this.loadData()
  }

  async loadMemberProfile () {
    this.memberProfileLoading = true
    try {
      const response = await this.apiClient.get<MemberResponse>(API.MEMBERS_API.PROFILE)
      this.$store.dispatch('setProfile', new MemberProfile(camelcaseKeys(response.profile)))
      this.$store.dispatch('setClub', new Club(camelcaseKeys(response.profile.club, { deep: true })))
    } catch (error) {
      console.error('Error loading member profile:', error)
    } finally {
      this.memberProfileLoading = false
    }
  }

  async loadData (loadInProgress = false, tab = 'myClub', dateFilter = 'today') {
    let clubId = null
    if (tab === 'myClub') {
      clubId = this.$store.state.club.id
    }

    await this.loadUserClubSummary()
    await this.loadLeaderBoard(clubId, dateFilter)
  }

  async loadUserClubSummary () {
    this.userClubSummaryLoading = true
    try {
      const userClubSummary = await this.userClubService.get()
      this.$store.dispatch('setUserClubSummary', userClubSummary)
    } catch (error) {
      console.error('Error in loadUserClubSummary:', error)
    } finally {
      this.userClubSummaryLoading = false
    }
  }

  async loadLeaderBoard (clubId: string | null, dateFilter: string) {
    this.leaderBoardLoading = true
    try {
      const leaderBoard = await this.userClubService.getLeaderBoard(clubId, dateFilter)
      this.$store.dispatch('setLeaderBoard', leaderBoard)
    } catch (error) {
      console.error('Error in loadLeaderBoard:', error)
    } finally {
      this.leaderBoardLoading = false
    }
  }

  async reloadDataUserClubSummary () {
    await this.loadUserClubSummary()
  }

  async reloadDataLeaderBoard (isReload: boolean) {
    const tab = this.myClub ? 'myClub' : 'allClubs'
    const dateFilter = this.dateFilter
    await this.loadLeaderBoard(tab === 'myClub' ? this.$store.state.club.id : null, dateFilter)
  }

  selectTab (tab: string) {
    return async () => {
      this.myClub = tab === 'myClub'
      this.allClubs = tab === 'allClubs'
      await this.loadData(true, tab, this.dateFilter)
    }
  }

  async updateDateFilter (value: string) {
    this.dateFilter = value
    console.log('dateFilter:', this.dateFilter)
    await this.loadData(true, this.myClub ? 'myClub' : 'allClubs', this.dateFilter)
  }

  isValidImageUrl (url: string): boolean {
    const img = new Image()
    img.src = url
    return img.complete && img.naturalHeight !== 0
  }

  render (h: CreateElement) {
    if (this.memberProfileLoading) {
      return <Loader />
    }

    const { freeStationsCount, inUseStationsCount, totalStationsCount, onlineStations } = this.userClubSummary
    const onlineStationsCount = freeStationsCount + inUseStationsCount
    const freeStationsPercentage = onlineStationsCount ? Math.round((freeStationsCount / onlineStationsCount) * 100) : 0
    const { top, around, member } = this.leaderBoard

    // Only hide the leaderboard if member data exists and hiddenFromLeaderboard is explicitly true
    const shouldShowLeaderboard = !this.member || this.member.hiddenFromLeaderboard !== true

    return (
      <div class='scroll-summary'>
        <div id={'user-club-summary-wrapper'}>
          <div id={'summary-wrapper'}>
            <div id={'my-club-background'}>
              <div class={this.userClubSummaryLoading ? 'in-progress reload-stations-button' : ' reload-stations-button'}>
                <Icon id='user-club-stations-refresh' name='util/refresh' click={this.reloadDataUserClubSummary}/>
              </div>
            </div>
            <div id={'data-wrapper'}>
              <div id={'station-summary-left'}>
                <Subtitle1 id={'station-summary-left-title'}>{this.$t('PAGE.MEMBERS.MY_CLUB.STATION_AVAILABILITY')}</Subtitle1>
                <div id={'user-club-chart-wrapper'}>
                  <div class='semi-donut'
                       style={`--percentage : ${freeStationsPercentage}; --fill: #4FACFE ;`}>
                    <Subtitle1>{`${freeStationsPercentage}%`}</Subtitle1>
                  </div>
                </div>
                <div id={'user-club-chart-legend'}>
                  <div class={'user-club-chart-legend-item'}>
                    <div class={'free-bullet'} />
                    <Subtitle1>{`${this.$t('PAGE.MEMBERS.MY_CLUB.FREE_STATIONS')}: ${freeStationsCount}`}</Subtitle1>
                  </div>
                  <div class={'user-club-chart-legend-item'}>
                    <div class={'in-use-bullet'} />
                    <Subtitle1>{`${this.$t('PAGE.MEMBERS.MY_CLUB.IN_USE_STATIONS')}: ${onlineStationsCount - freeStationsCount}`}</Subtitle1>
                  </div>
                </div>
              </div>
              <div id={'station-summary-right'}>
                <div id={'station-image-wrapper'}>
                  <img src={'/assets/img/total-stations.png'} />
                </div>
                <Subtitle1>{`${this.$t('PAGE.MEMBERS.MY_CLUB.TOTAL_STATIONS')}: ${totalStationsCount}`}</Subtitle1>
              </div>
            </div>
          </div>
          <div id={'stations-overview'} class='scroll-content'>
            <div id={'list-wrapper'}>
              {onlineStations && onlineStations!.map((station) => (
                <div class={'club-station-row'}>
                  <div class={'club-station-name'}>
                    <div class={station.inUse ? 'in-use-bullet' : 'free-bullet'} />
                    <Subtitle1>{station.displayName || station.name}</Subtitle1>
                  </div>
                  <Subtitle1
                    class={station.inUse ? 'in-use-label' : 'free-label'}
                  >{station.inUse ? this.$t('PAGE.MEMBERS.MY_CLUB.STATION_IN_USE') : this.$t('PAGE.MEMBERS.MY_CLUB.STATION_FREE')}</Subtitle1>
                </div>
              ))}
              {onlineStations && onlineStations.length === 0 && (
                <div class='no-stations'>
                  <Subtitle1>{this.$t('PAGE.MEMBERS.MY_CLUB.NO_DATA')}</Subtitle1>
                </div>
              )}
            </div>
          </div>
        </div>

        {shouldShowLeaderboard && (
        <div class='leaderboard-composition'>
          <div id='leaderboard-wrapper'>
            <div id='leaderboard-wrapper-score'>
              {this.leaderBoardLoading && (
                <Loader />
              )}
              {!this.leaderBoardLoading && (
              <div id='leaderboard-header'>
                <div class='refresh-leaderboard mobile-view'>
                  <button class='refresh-button' onClick={this.reloadDataLeaderBoard} ><Icon class='refresh-icon' name='util/material-refresh'/>
                    {(this.$t('PAGE.MEMBERS.MY_CLUB.REFRESH_LEADERBOARD') as string).toUpperCase()}
                  </button>
                </div>
                <p>
                  <strong>{this.$t('PAGE.MEMBERS.MY_CLUB.LEADERBOARD')}</strong>
                </p>
                <div id='tabs'>
                  <span onClick={this.selectTab('myClub')} class={this.myClub ? 'active' : ''}>{(this.$t('PAGE.MEMBERS.MY_CLUB.MY_CLUB') as string).toUpperCase()}</span>
                  <span onClick={this.selectTab('allClubs')} class={this.allClubs ? 'active' : ''}>{(this.$t('PAGE.MEMBERS.MY_CLUB.ALL_CLUBS') as string).toUpperCase()}</span>
                </div>
                  <div id='filter'>
                    <Select id='date-filter' label={this.$t('PAGE.MEMBERS.MY_CLUB.CHOOSE_DATE_FILTER')}
                            value={(this.$t(`PAGE.MEMBERS.MY_CLUB.${this.dateFilter.toUpperCase()}`) as string).toUpperCase()}
                    >
                      <OptionGroup>
                        <Option type='single-option' value='today' label={(this.$t('PAGE.MEMBERS.MY_CLUB.TODAY') as string).toUpperCase()}
                                selected={this.dateFilter === 'today'} select={this.updateDateFilter} ref='today' />
                        <Option type='single-option' value='yesterday' label={(this.$t('PAGE.MEMBERS.MY_CLUB.YESTERDAY') as string).toUpperCase()}
                                selected={this.dateFilter === 'yesterday'} select={this.updateDateFilter} ref='yesterday' />
                        <Option type='single-option' value='this_week' label={(this.$t('PAGE.MEMBERS.MY_CLUB.THIS_WEEK') as string).toUpperCase()}
                                selected={this.dateFilter === 'this_week'} select={this.updateDateFilter} ref='this_week' />
                        <Option type='single-option' value='last_week' label={(this.$t('PAGE.MEMBERS.MY_CLUB.LAST_WEEK') as string).toUpperCase()}
                                selected={this.dateFilter === 'last_week'} select={this.updateDateFilter} ref='last_week' />
                        <Option type='single-option' value='this_month' label={(this.$t('PAGE.MEMBERS.MY_CLUB.THIS_MONTH') as string).toUpperCase()}
                                selected={this.dateFilter === 'this_month'} select={this.updateDateFilter} ref='this_month' />
                        <Option type='single-option' value='last_month' label={(this.$t('PAGE.MEMBERS.MY_CLUB.LAST_MONTH') as string).toUpperCase()}
                                selected={this.dateFilter === 'last_month'} select={this.updateDateFilter} ref='last_month' />
                        <Option type='single-option' value='total' label={(this.$t('PAGE.MEMBERS.MY_CLUB.TOTAL') as string).toUpperCase()}
                                selected={this.dateFilter === 'total'} select={this.updateDateFilter} ref='total' />
                      </OptionGroup>
                    </Select>

                  </div>
                </div>
              )}
              {!this.leaderBoardLoading ? (
                top && top.length > 0 ? (
                <div class='leaderboard-composition'>
                  <div class='leaderboard-flex-row'>
                    <div class='leaderboard-title'>
                      <strong>{this.$t('PAGE.MEMBERS.MY_CLUB.TOP_3')}</strong>
                    </div>
                  </div>
                  <div id='leaderboard-content'>
                    <div id='leaderboard-top-3'>
                      {top.slice(0, 3).map((user, index) => (
                        <div
                          class={`leaderboard-position ${
                            index === 0 ? 'first' : index === 1 ? 'second' : 'third'
                          }`}
                          key={index}
                        >
                          <span class='points'>{user.score} {this.$t('PAGE.MEMBERS.MY_CLUB.MET')}</span>
                          <div class='leaderboard-bar'>
                            <span class='place'>
                              {index === 0
                                ? `${index + 1}`
                                : null}
                            </span>
                          </div>
                          <span class='name'>{user.username}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                ) : (
                  <div class='no-data-message'>{this.$t('PAGE.MEMBERS.MY_CLUB.NO_RANKING_DATA')}</div>
                )
              ) : null}
            </div>

            {!this.leaderBoardLoading && (
            <div id='rankings'>
              <div class='refresh-leaderboard web-view'>
                <button class='refresh-button' onClick={this.reloadDataLeaderBoard}>
                  <Icon class='refresh-icon' name='util/material-refresh'/>
                  {(this.$t('PAGE.MEMBERS.MY_CLUB.REFRESH_LEADERBOARD') as string).toUpperCase()}
                </button>
              </div>

              <div class='your-ranking'>
                <span class='p-20'>{this.$t('PAGE.MEMBERS.MY_CLUB.YOUR_RANKING')}</span>
                {member ? (
                  <div class='ranking-item' style={{ textAlign: 'left' }}>
                    <span class='rank-number color-leaderboard'>{member.rank}.</span>
                    <img src={member.avatar_thumb_url || '/assets/img/profile-picture.png'} alt='User image'/>
                    <span class='rank-name highlighted color-leaderboard'>{member.username}</span>
                    <span class='rank-points color-leaderboard'>{member.score} {this.$t('PAGE.MEMBERS.MY_CLUB.MET')}</span>
                  </div>
                ) : (
                  <div class='no-data-message pl-20'>{this.$t('PAGE.MEMBERS.MY_CLUB.NO_MEMBER_DATA_AVAILABLE')}</div>
                )}
              </div>

              <Divider/>

              <div class='ranking-list ranking-list-max-height'>
                <span class='p-20'>{this.$t('PAGE.MEMBERS.MY_CLUB.RANKING')}</span>
                {top && top.length > 0 ? (
                  top.map((user, index) => (
                    <div
                      class={`ranking-item ${index < 3 ? 'top-ranking' : ''}`}
                      key={index}
                    >
                      <span class={`rank-number ${member && index >= 3 && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.rank}.</span>
                      <img src={user.avatar_thumb_url || '/assets/img/profile-picture.png'} alt='User image'/>
                      <span class={`rank-name  ${member && index >= 3 && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.username}</span>
                      <span class={`rank-points  ${member && index >= 3 && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.score} {this.$t('PAGE.MEMBERS.MY_CLUB.MET')}</span>
                    </div>
                  ))
                ) : (
                  <div class='no-data-message pl-20'>{this.$t('PAGE.MEMBERS.MY_CLUB.NO_RANKING_DATA')}</div>
                )}
              </div>

              <Divider/>

              <div class='ranking-list around-list'>
                {around && around.length > 0 ? (
                  around.map((user, index) =>
                    index ? (
                      <div class='ranking-item' key={index}>
                        <span class={`rank-number ${member && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.rank}.</span>
                        <img
                          src={this.isValidImageUrl(user.avatar_thumb_url) ? user.avatar_thumb_url : '/assets/img/profile-picture.png'}
                          alt='User image'
                        />
                        <span class={`rank-name  ${member && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.username}</span>
                        <span class={`rank-points  ${member && member.id === user.id ? 'color-leaderboard' : ''}`}>{user.score} {this.$t('PAGE.MEMBERS.MY_CLUB.MET')}</span>
                      </div>
                    ) : null
                  )
                ) : null}
              </div>
            </div>
          )}
          </div>
        </div>
        )}
      </div>
    )
  }
}
